import React from 'react';
import { Topic } from '../../services/topicService';
import { UserService } from '../../services/userService';
import './TopicItem.css';

interface TopicItemProps {
  topic: Topic;
  user?: any; // Add user prop to check admin status
  onTopicClick?: (topic: Topic) => void;
  onStatusToggle?: (topicId: string, newStatus: 'ACTIVE' | 'PAUSED') => void;
  onTypeToggle?: (topicId: string, newType: 'USER' | 'COMMON') => void;
  onDelete?: (topicId: string) => void;
}

const TopicItem: React.FC<TopicItemProps> = ({ topic, user, onTopicClick, onStatusToggle, onTypeToggle, onDelete }) => {
  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return 'Less than a minute ago';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    } else if (diffInMinutes < 1440) { // Less than 24 hours
      const diffInHours = Math.floor(diffInMinutes / 60);
      return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    } else {
      const diffInDays = Math.floor(diffInMinutes / 1440);
      return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
    }
  };

  const formatCompactTime = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return diffInSeconds < 10 ? '<10s' : `${diffInSeconds}s`;
    }
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m`;
    }
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours}h`;
    }
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d`;
  };

  const handleClick = () => {
    if (onTopicClick) {
      onTopicClick(topic);
    }
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the topic click
    if (onStatusToggle && topic.id) {
      const newStatus = (topic.status || 'ACTIVE') === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
      onStatusToggle(topic.id, newStatus);
    }
  };

  const handleTypeClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the topic click
    if (onTypeToggle && topic.id) {
      const newType = (topic.type || 'USER') === 'USER' ? 'COMMON' : 'USER';
      onTypeToggle(topic.id, newType);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the topic click
    if (onDelete && topic.id) {
      onDelete(topic.id);
    }
  };

  return (
    <div 
      className={`topic-item ${onTopicClick ? 'clickable' : ''}`}
      onClick={handleClick}
    >
      <div className="topic-content">
        <div className="topic-header">
          <h3 className="topic-value">{topic.topicValue}</h3>
          <div className="topic-header-right">
            {topic.lastNewsItemAt ? (
              <span className="last-news-time-tag" title={`Last news item: ${formatRelativeTime(topic.lastNewsItemAt)}`}>
                {formatCompactTime(topic.lastNewsItemAt)}
              </span>
            ) : (
              <span className="last-news-time-tag" title="No news items yet">
                Never
              </span>
            )}
            {onDelete && (
              <button 
                className="unsubscribe-button"
                onClick={handleDeleteClick}
                title="Unsubscribe from topic"
              >
                Unsubscribe
              </button>
            )}
          </div>
        </div>
        {UserService.isAdmin(user) && (
          <div className="topic-meta">
            <div className="topic-stats">
              <span className="refresh-time-tag">
                {topic.refreshTime || 60}min
              </span>
              <span 
                className={`status-tag ${(topic.status || 'ACTIVE').toLowerCase()} ${onStatusToggle ? 'clickable' : ''}`}
                onClick={handleStatusClick}
              >
                {topic.status || 'ACTIVE'}
              </span>
              <span 
                className={`type-tag ${(topic.type || 'USER').toLowerCase()} ${onTypeToggle ? 'clickable' : ''}`}
                onClick={handleTypeClick}
              >
                {topic.type || 'USER'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopicItem;